import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, SmilePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/utils/cn";
import type { IChatMessage } from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { EmojiPicker } from "./EmojiPicker";
import {
  myReaction,
  reactionRows,
  reactionSummary,
} from "../_lib/reactions";
import { quickReactionsFor, recordEmojiUse, useFrequentEmojis } from "../_lib/emoji-usage";

/**
 * The smiley beside a bubble: a quick bar of six reactions — my most used
 * first, then the usual ones — and "+" for the full picker. Picking my
 * current emoji takes it back.
 */
export const ReactionButton = ({
  message,
  isMine,
  myUserId,
  onReact,
}: {
  message: IChatMessage;
  isMine: boolean;
  myUserId: number | undefined;
  onReact: (m: IChatMessage, emoji: string) => void;
}) => {
  const { t } = useTranslation("chat");
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  const mine = myReaction(message.reactions, myUserId);
  const quick = quickReactionsFor(useFrequentEmojis(myUserId));

  const pick = (emoji: string) => {
    setOpen(false);
    // Taking my reaction back isn't a use.
    if (emoji !== mine) recordEmojiUse(myUserId, emoji);
    onReact(message, emoji);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setFull(false);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={t("reactions.react")}
          title={t("reactions.react")}
          className="shrink-0"
        >
          <SmilePlus className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align={isMine ? "end" : "start"}
        className={cn("w-auto", full ? "rounded-md! p-2" : "rounded-full! p-1")}
      >
        {full ? (
          <EmojiPicker onPick={pick} selected={mine} />
        ) : (
          <div role="group" aria-label={t("reactions.quick")} className="flex items-center gap-0.5">
            {quick.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => pick(e)}
                aria-pressed={mine === e}
                aria-label={mine === e ? t("reactions.remove_emoji", { emoji: e }) : t("reactions.react_with", { emoji: e })}
                className={cn(
                  "flex size-9 items-center justify-center rounded-full text-xl transition-transform hover:scale-125 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden motion-reduce:transition-none motion-reduce:hover:scale-100",
                  mine === e && "bg-primary-muted",
                )}
              >
                {e}
              </button>
            ))}
            <Button
              variant="ghost"
              size="icon-sm"
              className="rounded-full"
              aria-label={t("reactions.more_emoji")}
              title={t("reactions.more_emoji")}
              onClick={() => setFull(true)}
            >
              <Plus className="size-4" />
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/**
 * Under a bubble, overlapping its bottom edge: up to three emoji and the total.
 * Opens who reacted with what; my own row takes my reaction back.
 */
export const ReactionPill = ({
  message,
  isMine,
  myUserId,
  memberNames,
  memberImages,
  canReact,
  onReact,
  className,
}: {
  message: IChatMessage;
  isMine: boolean;
  myUserId: number | undefined;
  memberNames: Map<number, string>;
  memberImages?: Map<number, string | null>;
  canReact: boolean;
  onReact: (m: IChatMessage, emoji: string) => void;
  className?: string;
}) => {
  const { t } = useTranslation("chat");
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);
  const reactions = message.reactions ?? [];
  if (!reactions.length || message.isDeleted) return null;

  const { emojis, total } = reactionSummary(reactions);
  const mine = myReaction(reactions, myUserId);
  const rows = reactionRows(reactions, myUserId).filter((r) => !filter || r.emoji === filter);

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setFilter(null);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("reactions.summary", { count: total })}
          className={cn(
            "relative z-[1] -mt-2 flex h-6 items-center gap-0.5 rounded-full border px-1.5 text-sm leading-none shadow-xs transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden",
            mine
              ? "border-primary/40 bg-primary-muted hover:bg-primary-muted/80"
              : "border-border bg-surface hover:bg-muted",
            className,
          )}
        >
          {emojis.map((e) => (
            <span key={e} aria-hidden>
              {e}
            </span>
          ))}
          <span
            aria-hidden
            className={cn(
              "ms-0.5 text-[11px] font-medium tabular-nums",
              mine ? "text-primary" : "text-muted-foreground",
            )}
          >
            {total}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align={isMine ? "end" : "start"} className="w-64 p-0">
        <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-border p-1.5">
          <FilterTab active={!filter} onClick={() => setFilter(null)}>
            {t("reactions.all", { total })}
          </FilterTab>
          {reactions.map((r) => (
            <FilterTab key={r.emoji} active={filter === r.emoji} onClick={() => setFilter(r.emoji)}>
              <span aria-hidden>{r.emoji}</span>
              <span className="tabular-nums">{r.count}</span>
            </FilterTab>
          ))}
        </div>
        <ul className="max-h-64 overflow-y-auto p-1">
          {rows.map((r) => {
            const isMe = r.userId === myUserId;
            const name = isMe
              ? t("reactions.you")
              : (memberNames.get(r.userId) ?? t("system.someone"));
            const content = (
              <>
                <ChatAvatar
                  name={memberNames.get(r.userId) ?? name}
                  imageUrl={memberImages?.get(r.userId)}
                  size="sm"
                />
                <span className="flex min-w-0 flex-1 flex-col text-start">
                  <span className="truncate text-sm font-medium text-foreground">{name}</span>
                  {isMe && canReact ? (
                    <span className="text-xs text-muted-foreground">{t("reactions.click_to_remove")}</span>
                  ) : null}
                </span>
                <span aria-hidden className="text-xl">
                  {r.emoji}
                </span>
              </>
            );
            return (
              <li key={`${r.userId}-${r.emoji}`}>
                {isMe && canReact ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onReact(message, r.emoji);
                    }}
                    aria-label={t("reactions.remove_emoji", { emoji: r.emoji })}
                    className="flex w-full items-center gap-3 rounded-sm px-2 py-1.5 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
                  >
                    {content}
                  </button>
                ) : (
                  <div className="flex items-center gap-3 px-2 py-1.5">{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

const FilterTab = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={cn(
      "flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden",
      active ? "bg-primary-muted text-primary" : "text-muted-foreground hover:bg-muted",
    )}
  >
    {children}
  </button>
);
